import { Component, OnDestroy, OnInit } from '@angular/core';
import { MeasurementPrinciple, MeterDto, MeterService as NtsMeterService } from '@app/api/nts';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ModelDto,
  ModelService,
  CtTurnOverRatio,
  MeterAccuracyClass,
  VtTurnOverRatio,
  Quadrant,
  VendorDto,
  VendorService,
  MeterService as DmcMeterService,
  UpdateMeterDeviationLimitsDto
} from '@app/api/dmc';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { Subscription } from 'rxjs';
import { FormApiService } from '@app/core/services/form-api.service';
import { TranslatePipe } from '@ngx-translate/core';
import { logFormErrors } from '@app/core/utils/log-form-errors';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { InputFixedDigitsDirective } from '@shared/components/input/fixeddigits/input-fixeddigits.directive';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-meter-openform',
  imports: [
    ReactiveFormsModule,
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    SelectDirective,
    TranslatePipe,
    InputDateDirective,
    InputFixedDigitsDirective,

  ],
  templateUrl: './meter-openform.component.html',
  styleUrl: './meter-openform.component.css'
})
export class MeterOpenformComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public currentMeterdto?: MeterDto;
  public defaultMeterDto?: MeterDto;
  public meterFrameId?: string;
  public vendors?: VendorDto[] = [];
  public meterModels?: ModelDto[] = [];
  public form: FormGroup = new FormGroup({
    id: new FormControl(null),
    commissioningDate: new FormControl(null, Validators.required),
    meterNumber: new FormControl(null, Validators.required),
    alternateMeterNumber: new FormControl(null, Validators.required), serialNumber: new FormControl(null, Validators.required),
    vendor: new FormGroup({
      id: new FormControl(null),
      name: new FormControl(null, Validators.required),
    }),
    model: new FormGroup({
      id: new FormControl(null),
      name: new FormControl(null, Validators.required),
      vendorId: new FormControl(null),
    }),
    productionYear: new FormControl(null, Validators.required),
    numberOfDigits: new FormControl(null, Validators.required),
    meterReadingType: new FormControl(null, Validators.required),
    status: new FormControl(null),
    meterFrameId: new FormControl(null),
    // Mapping values are set by backend
    mappingA14: new FormControl(null),
    mappingA23: new FormControl(null),
    mappingR12: new FormControl(null),
    mappingR34: new FormControl(null),
    acquisitionDate: new FormControl(null),
    accuracyClass: new FormControl(null),
    measurementPrinciple: new FormControl(null),
    comment: new FormControl(null),
    ctTurnOverRatio: new FormControl(null),
    vtTurnOverRatio: new FormControl(null),
    relativeDeviation: new FormControl(null),
    absoluteDeviation: new FormControl(null),
    // Conversion Factor
    conversionFactorA14: new FormControl(1, Validators.required),
    conversionFactorA23: new FormControl(1, Validators.required),
    conversionFactorR12: new FormControl(1, Validators.required),
    conversionFactorR34: new FormControl(1, Validators.required),
  });
  /** Dropdowns **/
  public quadrant: KeyValue[] = enumToKeyValue(Quadrant, {omitKeys: ['NotSpecified'], prefixValue: 'enum.quadrant.'});
  private readonly subscriptions: Subscription = new Subscription();

  public accuracyClassList: KeyValue[] = enumToKeyValue(MeterAccuracyClass, { omitKeys: ['NotSpecified'], prefixValue: 'meter.accuracyClasses.' });
  public measurementPrincipleList: KeyValue[] = enumToKeyValue(MeasurementPrinciple, { omitKeys: ['NotSpecified'], prefixValue: 'meter.measurementPrinciples.' });
  public ctTurnOverRatioList: KeyValue[] = enumToKeyValue(CtTurnOverRatio, { prefixValue: 'meter.ctTurnOverRatios.' });
  public vtTurnOverRatioList: KeyValue[] = enumToKeyValue(VtTurnOverRatio, { prefixValue: 'meter.vtTurnOverRatios.' });

  constructor(
    private readonly ntsMeterService: NtsMeterService,
    private readonly dmcMeterService: DmcMeterService,
    private readonly vendorService: VendorService,
    private readonly formApiService: FormApiService,
    private readonly meterModelService: ModelService
  ) {
  }

  public ngOnInit(): void {
    this.vendorService.vendorAllGet().subscribe((result: VendorDto[]) => {
      this.vendors = result

      if (this.currentMeterdto) {
        const formValues = { ...this.currentMeterdto };
        if (formValues.commissioningDate) {
          const date = new Date(formValues.commissioningDate);
          formValues.commissioningDate = date.toISOString().split('T')[0];
        }
        if (formValues.acquisitionDate) {
          const date = new Date(formValues.acquisitionDate);
          formValues.acquisitionDate = date.toISOString().split('T')[0]; // This gives yyyy-MM-dd
        }

        this.form.patchValue(formValues);

        if (this.currentMeterdto.vendor?.id) {
          this.meterModelService.modelFromvendorGet(this.currentMeterdto.vendor.id).subscribe(
            (result: ModelDto[]) => {
              this.meterModels = result;

              if (this.currentMeterdto?.model) {
                this.form.get('model.name')?.setValue(this.currentMeterdto.model.name);
                this.form.get('model.id')?.setValue(this.currentMeterdto.model.id);
                this.form.get('model.vendorId')?.setValue(this.currentMeterdto.model.vendorId);
              }
            }
          );
        }
      } else {
        this.ntsMeterService.meterGetDefaultValuesGet().subscribe((result: MeterDto) => {
          this.defaultMeterDto = result;
          const formValues = {...this.defaultMeterDto};
          this.form.patchValue(formValues);

          // Set default vendor from isDefault flag
          const defaultVendor = this.vendors?.find(v => v.isDefault);
          if (defaultVendor?.id) {
            this.form.get('vendor.name')?.setValue(defaultVendor.name, { emitEvent: false });
            this.form.get('vendor.id')?.setValue(defaultVendor.id);

            // Load models for the default vendor and set default model
            this.meterModelService.modelFromvendorGet(defaultVendor.id).subscribe(
              (models: ModelDto[]) => {
                this.meterModels = models;

                const defaultModel = models.find(m => m.isDefault);
                if (defaultModel) {
                  this.form.get('model.name')?.setValue(defaultModel.name);
                  this.form.get('model.id')?.setValue(defaultModel.id);
                  this.form.get('model.vendorId')?.setValue(defaultModel.vendorId);
                }
              }
            );
          }
        });
      }
    });

    this.subscriptions.add(
      this.form.get('vendor.name')?.valueChanges.subscribe(vendorName => {
        const vendor = this.vendors?.find(v => v.name === vendorName);
        this.form.get('vendor.id')?.setValue(vendor?.id);
        if (vendor?.id) {
          this.meterModelService.modelFromvendorGet(vendor.id).subscribe(
            (result: ModelDto[]) => {
              this.meterModels = result;

              const modelExists = this.meterModels.some(m => m.name === this.currentMeterdto?.model?.name);

              if (!modelExists || !this.currentMeterdto) {
                this.form.get('model.name')?.setValue('');
                this.form.get('model.id')?.setValue(null);
              } else {
                this.form.get('model.name')?.setValue(this.currentMeterdto.model?.name);
                const model = this.meterModels?.find(m => m.name === this.currentMeterdto!.model?.name);
                this.form.get('model.id')?.setValue(model?.id);
                this.form.get('model.vendorId')?.setValue(model?.vendorId);
              }
            }
          );
        }
      }));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public saveChanges(): void {
    if (this.form.valid) {
      const modelName = this.form.get('model.name')?.value;
      const model = this.meterModels?.find(m => m.name === modelName);
      this.form.get('model.id')?.setValue(model?.id);

      // Update meter
      this.formApiService.submit(
        this.form,
        (data) => this.ntsMeterService.meterEditPost(data),
        {
          successMessage: 'meter.meterUpdated',
          closeModal: false,
        }
      );

      const meterDeviationLimits: UpdateMeterDeviationLimitsDto = {
        meterId: this.form.get('id')?.value,
        quadrant: this.currentMeterdto?.quadrant,
        relativeDeviation: this.form.get('relativeDeviation')?.value,
        absoluteDeviation: this.form.get('absoluteDeviation')?.value
      };

      // Update deviation limits
      this.formApiService.submit(
        this.form,
        () => this.dmcMeterService.meterUpdateMeterDeviationLimitsPost(meterDeviationLimits),
        {
          closeModal: true,
        }
      );
    } else {
      this.form.markAllAsTouched();
    }
  }

  public createMeter(): void {
    if (this.meterFrameId) {
      this.form.get('meterFrameId')?.setValue(this.meterFrameId);
    }
    logFormErrors(this.form)
    if (this.form.valid) {
      const modelName = this.form.get('model.name')?.value;
      const model = this.meterModels?.find(m => m.name === modelName);
      this.form.get('model.id')?.setValue(model?.id);
      this.form.get('commissioningDate')?.setValue(new Date(this.form.get('commissioningDate')?.value).toISOString().split('T')[0]);
      this.formApiService.submit(
        this.form,
        (data) => this.ntsMeterService.meterNewPost(data),
        {
          successMessage: 'meter.meterCreated',
          closeModal: true,
        }
      );
    } else {
      this.form.markAllAsTouched();
    }
  }

  public createMeterWithoutAttach(): void {
    this.form.get('meterFrameId')?.setValue(null);

    logFormErrors(this.form)
    if (this.form.valid) {
      const modelName = this.form.get('model.name')?.value;
      const model = this.meterModels?.find(m => m.name === modelName);
      this.form.get('model.id')?.setValue(model?.id);
      this.form.get('commissioningDate')?.setValue(new Date(this.form.get('commissioningDate')?.value).toISOString().split('T')[0]);

      this.formApiService.submit(
        this.form,
        (data) => this.ntsMeterService.meterAddMeterWithoutAttachingPost(data),
        {
          successMessage: 'meter.meterCreated',
          closeModal: true,
        }
      );
    } else {
      this.form.markAllAsTouched();
    }
  }

  public updateMeterValue(value: string): void {
    if (!this.form.get('meterNumber')?.value) {
      this.form.get('meterNumber')?.setValue(value);
    }

    if (!this.form.get('alternateMeterNumber')?.value) {
      this.form.get('alternateMeterNumber')?.setValue(value);
    }

    if (!this.form.get('serialNumber')?.value) {
      this.form.get('serialNumber')?.setValue(value);
    }
  }
}
